import React, { useContext, useEffect, useState } from "react";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import IQBOFindEstimateInvoicesDTO from "../../../models/DTO/IQBOFindEstimateInvoicesDTO";
import IQBOOnFindEstimateInvoicesDTO, {
    IQBOEstimateInvoiceCandidateDTO,
    IQBOEstimateInvoiceLookupDTO,
} from "../../../models/DTO/IQBOOnFindEstimateInvoicesDTO";
import QBOEstimateInvoiceDiagnosisEnum from "../../../models/QBOEstimateInvoiceDiagnosisEnum";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import { SocketContext } from "../../../contexts/SocketContext";
import UIService from "../../../services/UIService";
import UserService from "../../../services/UserService";
import OwButton from "../../shared/OwButton/OwButton";

// Staff diagnostic behind showHiddenFeatures, so the copy is plain English (like QboReprocessOrderAssignments).

type Props = {
    cancel: () => void;
    orderGuid: string;
};

const money = (amount: number | undefined | null): string =>
    amount === undefined || amount === null
        ? ""
        : Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const invoiceName = (invoice: IQBOEstimateInvoiceCandidateDTO): string => `invoice ${invoice.docNumber || invoice.invoiceId}`;

const isLinked = (invoice: IQBOEstimateInvoiceCandidateDTO): boolean =>
    invoice.headerLinksEstimate || invoice.linesLinkEstimate || invoice.estimateListsInvoice;

const isFullLineMatch = (invoice: IQBOEstimateInvoiceCandidateDTO): boolean =>
    invoice.estimateLines > 0 && invoice.matchedLines === invoice.estimateLines && invoice.matchedLines === invoice.invoiceLines;

/**
 * The invoice a diagnosis is about. The API sorts linked invoices first, then matching ones.
 */
const subjectInvoice = (lookup: IQBOEstimateInvoiceLookupDTO): IQBOEstimateInvoiceCandidateDTO | undefined => {
    switch (lookup.diagnosis) {
        case QBOEstimateInvoiceDiagnosisEnum.ORDER_LINKED:
            return lookup.invoices.find((invoice) => invoice.linkedOrder?.isThisOrder);
        case QBOEstimateInvoiceDiagnosisEnum.INVOICE_LINKS_ESTIMATE:
            return lookup.invoices.find((invoice) => invoice.headerLinksEstimate);
        case QBOEstimateInvoiceDiagnosisEnum.LINK_NOT_ON_INVOICE:
            return lookup.invoices.find(isLinked);
        case QBOEstimateInvoiceDiagnosisEnum.UNLINKED_MATCH:
            return lookup.invoices[0];
        default:
            return undefined;
    }
};

/**
 * Plain-English summary of a lookup: what QuickBooks shows, and why the invoice webhook did or did not link it.
 */
export const describeLookup = (lookup: IQBOEstimateInvoiceLookupDTO): string => {
    const invoice = subjectInvoice(lookup);
    const name = invoice ? invoiceName(invoice) : "an invoice";

    let summary: string;

    switch (lookup.diagnosis) {
        case QBOEstimateInvoiceDiagnosisEnum.NO_ESTIMATE:
            return "This order has no QuickBooks estimate.";

        case QBOEstimateInvoiceDiagnosisEnum.ESTIMATE_NOT_FOUND:
            return `QuickBooks has no estimate ${lookup.order.qbo_EstimateId}. It was probably deleted in QuickBooks.`;

        case QBOEstimateInvoiceDiagnosisEnum.ORDER_LINKED:
            return `This order is already linked to QuickBooks invoice ${lookup.order.qbo_InvoiceId || lookup.order.qbo_EntityId}.`;

        case QBOEstimateInvoiceDiagnosisEnum.INVOICE_LINKS_ESTIMATE:
            summary =
                `QuickBooks ${name} names this estimate on the invoice itself, which is what the invoice webhook reads.` +
                (invoice?.linkedOrder
                    ? ""
                    : " Its webhook was not processed (auto ship off, never delivered, or failed): search the logs for its invoice id.");
            break;

        case QBOEstimateInvoiceDiagnosisEnum.LINK_NOT_ON_INVOICE: {
            const where =
                invoice?.estimateListsInvoice && invoice.linesLinkEstimate
                    ? "on the estimate and on the invoice lines"
                    : invoice?.estimateListsInvoice
                      ? "on the estimate"
                      : "on the invoice lines";

            summary = `QuickBooks links ${name} to this estimate only ${where}. The invoice webhook only reads the link on the invoice itself, so it skipped this invoice.`;
            break;
        }

        case QBOEstimateInvoiceDiagnosisEnum.UNLINKED_MATCH:
            summary = `QuickBooks has no link, but ${name} has the same ${
                invoice && isFullLineMatch(invoice) ? "lines" : "total"
            } as this estimate. It was probably made by copying the estimate rather than converting it.`;
            break;

        default:
            return "No invoice for this customer since the estimate date is linked to this estimate or matches it.";
    }

    if (invoice?.linkedOrder && !invoice.linkedOrder.isThisOrder) {
        summary += ` Order ${invoice.linkedOrder.name} already holds this invoice.`;
    }

    return summary;
};

const alertClass = (diagnosis: QBOEstimateInvoiceDiagnosisEnum): string => {
    switch (diagnosis) {
        case QBOEstimateInvoiceDiagnosisEnum.ORDER_LINKED:
            return "alert-success";
        case QBOEstimateInvoiceDiagnosisEnum.NO_ESTIMATE:
        case QBOEstimateInvoiceDiagnosisEnum.INVOICE_LINKS_ESTIMATE:
            return "alert-info";
        default:
            return "alert-warning";
    }
};

const InvoiceCard: React.FC<{ invoice: IQBOEstimateInvoiceCandidateDTO }> = ({ invoice }) => (
    <li className="list-group-item" aria-label={`Invoice ${invoice.docNumber || invoice.invoiceId}`}>
        <div className="d-flex justify-content-between">
            <strong>Invoice {invoice.docNumber || "(no number)"}</strong>
            <span className="text-muted small">id {invoice.invoiceId}</span>
        </div>
        <div className="small">
            {UserService.parseUTCDate(invoice.txnDate)} · Total {money(invoice.totalAmt)} · Balance {money(invoice.balance)}
        </div>
        {invoice.createTime ? (
            <div className="small text-muted">Created in QuickBooks {UserService.parseDate(invoice.createTime)}</div>
        ) : null}
        <div className="mt-1 d-flex flex-wrap gap-1">
            {invoice.headerLinksEstimate ? <span className="badge bg-success">Invoice names estimate</span> : null}
            {invoice.linesLinkEstimate ? <span className="badge bg-warning text-dark">Invoice lines name estimate</span> : null}
            {invoice.estimateListsInvoice ? <span className="badge bg-warning text-dark">Estimate names invoice</span> : null}
            {!isLinked(invoice) ? <span className="badge bg-secondary">No link</span> : null}
        </div>
        <div className="small mt-1">
            Lines matched: {invoice.matchedLines} of {invoice.estimateLines} on the estimate ({invoice.invoiceLines} on the invoice) · Total{" "}
            {invoice.totalMatches ? "matches" : "differs"}
        </div>
        {invoice.headerLinkedTxn.length ? (
            <div className="small text-muted">Invoice LinkedTxn: {invoice.headerLinkedTxn.join(", ")}</div>
        ) : null}
        {invoice.linkedOrder ? (
            <div className="small mt-1">
                {invoice.linkedOrder.isThisOrder
                    ? "Linked to this order"
                    : `Held by order ${invoice.linkedOrder.name}${
                          invoice.linkedOrder.order_state_id
                              ? ` (${UIService.getOrderStateNameFromId(invoice.linkedOrder.order_state_id) || invoice.linkedOrder.order_state_id})`
                              : ""
                      }`}
            </div>
        ) : null}
    </li>
);

/**
 * Staff diagnostic for the estimate -> invoice auto ship flow: which QuickBooks invoice (if any) this order's
 * estimate became, and where QuickBooks records the link. Read-only.
 */
export const QBOEstimateInvoiceLookupSidebar: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>("");
    const [lookup, setLookup] = useState<IQBOEstimateInvoiceLookupDTO | null>(null);

    const onFindEstimateInvoices = (data: IQBOOnFindEstimateInvoicesDTO) => {
        if (data.orderGuid !== props.orderGuid) {
            return;
        }

        setLoading(false);
        setError(data.error || "");
        setLookup(data.lookup || null);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.QBOOnFindEstimateInvoices, fn: onFindEstimateInvoices }];

    const findEstimateInvoices = () => {
        const payload: IQBOFindEstimateInvoicesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid: props.orderGuid,
        };

        setLoading(true);
        setError("");
        socket.emit(EndpointsEnum.QBOFindEstimateInvoices, payload);
    };

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        findEstimateInvoices();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const estimate = lookup?.estimate;

    return (
        <div className={"QBOEstimateInvoiceLookupSidebar p-2"}>
            <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">QuickBooks invoice lookup</h5>
                <div>
                    <OwButton
                        className={"btn-sm btn-primary me-2"}
                        leftIconClass={"fas fa-sync"}
                        title={"Refresh"}
                        skipTranslate={true}
                        active={loading}
                        disableOnActive={true}
                        fn={findEstimateInvoices}
                    />
                    <OwButton centerIconClass={"far fa-times"} className={"btn-danger"} fn={() => props.cancel()} />
                </div>
            </div>

            {loading ? (
                <div className="mt-3" role="status">
                    Looking up QuickBooks...
                </div>
            ) : error ? (
                <div className="alert alert-danger mt-3" role="alert">
                    The lookup failed ({error}). The server log has the details.
                </div>
            ) : lookup ? (
                <div className="mt-3">
                    <div className={`alert ${alertClass(lookup.diagnosis)}`} role="alert">
                        {describeLookup(lookup)}
                    </div>

                    {estimate ? (
                        <div className="mb-3 small">
                            <div>
                                <strong>
                                    Estimate {estimate.docNumber || "(no number)"} (id {estimate.id})
                                </strong>{" "}
                                · {estimate.txnStatus} · {UserService.parseUTCDate(estimate.txnDate)} · Total {money(estimate.totalAmt)} ·{" "}
                                {estimate.lineCount} item lines
                            </div>
                            <div>
                                Customer: {estimate.customerName} (id {estimate.customerId})
                            </div>
                            <div>
                                Invoices named on the estimate:{" "}
                                {estimate.linkedInvoiceIds.length ? estimate.linkedInvoiceIds.join(", ") : "none"}
                            </div>
                        </div>
                    ) : null}

                    {estimate ? (
                        lookup.invoices.length ? (
                            <>
                                <div className="small text-muted mb-1">
                                    Customer invoices dated on or after the estimate, linked and matching first.
                                </div>
                                <ul className="list-group">
                                    {lookup.invoices.map((invoice) => (
                                        <InvoiceCard key={invoice.invoiceId} invoice={invoice} />
                                    ))}
                                </ul>
                                {lookup.candidatesTruncated ? (
                                    <div className="small text-muted mt-2">
                                        Only the earliest invoices after the estimate date were checked; the customer has more.
                                    </div>
                                ) : null}
                            </>
                        ) : (
                            <div className="small">This customer has no invoices dated on or after the estimate.</div>
                        )
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};
